"use client";

import { useState, type FormEvent } from "react";

const SIGNUP_URL = "https://platform.hudson-labs.com/auth/signup";

/**
 * Shared email-only lead capture used by the sidebar access card and the
 * mobile bottom banner.
 *
 * Posts to the same lead endpoint as the trial modal (`/api/leads/trial`) —
 * name fields are omitted entirely (they're optional server-side, and Copper
 * derives a display name from the email). `useCase` tags the source so the two
 * surfaces stay distinguishable in Copper.
 *
 * On successful lead creation the visitor is handed off to the platform signup
 * page with the email pre-filled.
 */
export function useCoAnalystSignup({
  useCase,
  token,
  resetTurnstile,
}: {
  useCase: string;
  token: string | null;
  resetTurnstile: () => void;
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    if (!token) {
      setError("Please complete the verification.");
      return;
    }

    setSubmitting(true);
    setError(null);
    setFieldError(null);

    try {
      const res = await fetch("/api/leads/trial", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, useCase, turnstileToken: token }),
      });

      const data = await res.json();

      if (!res.ok) {
        const details = data.details as Record<string, string[]> | undefined;
        if (details?.email?.[0]) {
          setFieldError(details.email[0]);
          setError("Please enter a valid email address.");
        } else {
          setError(data.error || "Something went wrong.");
        }
        resetTurnstile();
        setSubmitting(false);
        return;
      }

      // Lead created — hand off to the platform signup with the email
      // pre-filled. Keep `submitting` true so the form can't be re-submitted
      // while the browser navigates away.
      setRedirecting(true);
      const params = new URLSearchParams({ email: email.trim() });
      window.location.href = `${SIGNUP_URL}?${params.toString()}`;
    } catch {
      setError("Network error. Please try again.");
      resetTurnstile();
      setSubmitting(false);
    }
  };

  return {
    email,
    setEmail,
    submitting,
    redirecting,
    error,
    fieldError,
    handleSubmit,
  };
}
